import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Menu,
  QrCode,
  Smartphone,
  Store,
  LayoutDashboard,
  BadgeCheck,
  Clock3,
  Star,
  MapPin,
  ChevronRight,
  Wallet,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const WHATSAPP_URL =
  "https://wa.me/905533158324?text=Merhaba%2C%20QR%20Men%C3%BC%20hizmetiniz%20hakk%C4%B1nda%20bilgi%20almak%20istiyorum.%20Restoran%C4%B1m%20i%C3%A7in%20bilgi%20alabilir%20miyim%3F";

const previewCategories = ["Öne Çıkanlar", "Burger", "Pizza", "İçecekler"];

const previewItems = [
  {
    name: "Trüf Burger",
    desc: "Özel sos, cheddar, karamelize soğan",
    price: "245₺",
    image:
      "https://images.unsplash.com/photo-1568901346375-23c9450c58cd?auto=format&fit=crop&w=800&q=80",
  },
  {
    name: "Margarita Pizza",
    desc: "Mozzarella, domates sosu, fesleğen",
    price: "230₺",
    image:
      "https://images.unsplash.com/photo-1513104890138-7c749659a591?auto=format&fit=crop&w=800&q=80",
  },
  {
    name: "Ev Yapımı Limonata",
    desc: "Taze limon, nane, buz",
    price: "65₺",
    image:
      "https://images.unsplash.com/photo-1513558161293-cdaf765ed2fd?auto=format&fit=crop&w=800&q=80",
  },
];

export default function LandingPage() {
  const navigate = useNavigate();

  const openWhatsapp = () =>
    window.open(WHATSAPP_URL, "_blank", "noopener,noreferrer");

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (!element) return;

    element.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-white scroll-smooth">
      <nav className="sticky top-0 z-30 border-b border-zinc-100 bg-white/90 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-3 px-4 sm:px-6 lg:px-8">
          <button
            type="button"
            onClick={() => scrollToSection("top")}
            className="flex items-center gap-2"
          >
            <div className="grid h-10 w-10 place-items-center rounded-2xl bg-zinc-900 text-white">
              <QrCode className="h-5 w-5" />
            </div>
            <div className="leading-tight">
              <div className="text-sm font-semibold text-zinc-900">
                Megriva QR Menü
              </div>
            </div>
          </button>

          <div className="hidden items-center gap-2 md:flex">
            <button
              type="button"
              onClick={() => scrollToSection("how-it-works")}
              className="rounded-lg px-3 py-2 text-sm text-zinc-600 transition hover:bg-zinc-100 hover:text-zinc-900"
            >
              Nasıl Çalışır
            </button>

            <button
              type="button"
              onClick={() => scrollToSection("features")}
              className="rounded-lg px-3 py-2 text-sm text-zinc-600 transition hover:bg-zinc-100 hover:text-zinc-900"
            >
              Özellikler
            </button>

            <button
              type="button"
              onClick={() => scrollToSection("pricing")}
              className="rounded-lg px-3 py-2 text-sm text-zinc-600 transition hover:bg-zinc-100 hover:text-zinc-900"
            >
              Fiyatlandırma
            </button>

            <button
              type="button"
              onClick={() => scrollToSection("trial")}
              className="rounded-lg px-3 py-2 text-sm text-zinc-600 transition hover:bg-zinc-100 hover:text-zinc-900"
            >
              Ücretsiz Dene
            </button>
          </div>

          <div className="flex min-w-0 items-center gap-2 sm:gap-3">
            <Button
              variant="ghost"
              onClick={() => navigate("/login")}
              className="px-3 sm:px-4"
              data-testid="landing-login-button"
            >
              Giriş Yap
            </Button>

            <Button
              onClick={() => navigate("/register")}
              className="rounded-full bg-zinc-900 px-4 sm:px-5 hover:bg-zinc-800"
              data-testid="landing-register-button"
            >
              Hemen Başla
            </Button>
          </div>
        </div>
      </nav>

      <section id="top" className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-orange-50/60 via-white to-white" />

        <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 md:py-24">
          <div className="grid items-center gap-14 md:grid-cols-2">
            <div className="text-center md:text-left">
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-1.5 text-xs font-medium text-orange-700">
                <BadgeCheck className="h-4 w-4" />
                14 gün ücretsiz deneyin
              </div>

              <h1
                className="text-4xl font-bold leading-tight tracking-tight text-zinc-900 md:text-6xl"
                style={{ fontFamily: "Playfair Display, serif" }}
                data-testid="hero-heading"
              >
                QR Menü ile Restoranınızı Dijitale Taşıyın
              </h1>

              <p
                className="mt-6 text-lg leading-relaxed text-zinc-600 md:text-xl"
                style={{ fontFamily: "Plus Jakarta Sans, sans-serif" }}
              >
                Dakikalar içinde dijital menünüzü oluşturun, QR kod ile
                müşterilerinize sunun. Menülerinizi panelden yönetin, fiyatları
                ve içerikleri dilediğiniz zaman güncelleyin.
              </p>

              <div className="mt-8 flex flex-col gap-4 sm:flex-row md:justify-start justify-center">
                <Button
                  size="lg"
                  onClick={() => navigate("/register")}
                  className="h-12 rounded-full bg-orange-500 px-8 text-white hover:bg-orange-600"
                  data-testid="hero-get-started-button"
                >
                  14 Gün Ücretsiz Başla
                </Button>

                <Button
                  size="lg"
                  variant="outline"
                  onClick={openWhatsapp}
                  className="h-12 rounded-full border-zinc-300 px-8 hover:bg-zinc-50"
                  data-testid="hero-view-demo-button"
                >
                  Detaylı Bilgi
                </Button>
              </div>

              <div className="mt-8 flex flex-wrap gap-6 text-xs text-zinc-500 md:justify-start justify-center">
                <span>✔ 2 dakikada kurulum</span>
                <span>✔ QR otomatik oluşur</span>
                <span>✔ Panelden yönetim</span>
              </div>
            </div>

            <div className="flex justify-center md:justify-end">
              <div className="relative">
                <div className="absolute right-2 bottom-14 hidden rounded-2xl border border-emerald-100 bg-white px-4 py-3 shadow-lg lg:block">
                  <div className="text-xs text-zinc-500">Anlık güncelleme</div>
                  <div className="mt-1 text-sm font-semibold text-zinc-900">
                    Menü değişikliği anında yayında
                  </div>
                </div>

                <div className="absolute right-2 bottom-14 hidden rounded-2xl border border-emerald-100 bg-white px-4 py-3 shadow-lg lg:block">
                  <div className="text-xs text-zinc-500">Mobil uyumlu</div>
                  <div className="mt-1 text-sm font-semibold text-zinc-900">
                    iOS & Android destekli
                  </div>
                </div>

                <div className="w-[320px] rounded-[42px] border border-zinc-200 bg-zinc-900 p-2.5 shadow-[0_30px_80px_rgba(0,0,0,0.18)] md:w-[360px]">
                  <div className="relative overflow-hidden rounded-[34px] bg-white">
                    <div className="flex h-7 items-center justify-center gap-1.5 bg-zinc-950">
                      <div className="h-1.5 w-1.5 rounded-full bg-zinc-600" />
                      <div className="h-1.5 w-1.5 rounded-full bg-zinc-600" />
                      <div className="h-1.5 w-1.5 rounded-full bg-zinc-600" />
                    </div>

                    <div className="h-[640px] overflow-hidden">
                      <div className="h-full overflow-y-auto">
                        <div className="relative h-44 overflow-hidden">
                          <img
                            src="https://images.unsplash.com/photo-1552566626-52f8b828add9?auto=format&fit=crop&w=1200&q=80"
                            alt="Restoran banner"
                            className="h-full w-full object-cover"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/10 to-transparent" />

                          <div className="absolute left-4 right-4 top-4 flex items-start justify-between gap-3">
                            <div className="rounded-full bg-white/90 px-3 py-1 text-[11px] font-medium text-zinc-900 backdrop-blur">
                              Demo Menü
                            </div>

                            <div className="rounded-full bg-emerald-500 px-3 py-1 text-[11px] font-medium text-white shadow">
                              Açık
                            </div>
                          </div>

                          <div className="absolute bottom-4 left-4 right-4 text-white">
                            <div className="flex items-center gap-2 text-[11px] text-white/85">
                              <MapPin className="h-3.5 w-3.5" />
                              Çorum / Merkez
                            </div>

                            <h3 className="mt-2 text-2xl font-bold">
                              Lezzet Bahçesi
                            </h3>

                            <div className="mt-2 flex items-center gap-3 text-xs text-white/90">
                              <span className="inline-flex items-center gap-1">
                                <Star className="h-3.5 w-3.5 fill-current" />
                                4.8
                              </span>
                              <span className="inline-flex items-center gap-1">
                                <Clock3 className="h-3.5 w-3.5" />
                                11:00 - 23:30
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="border-b border-zinc-100 bg-white px-4 py-3">
                          <div className="rounded-2xl bg-orange-50 px-4 py-3">
                            <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-orange-600">
                              Kampanya
                            </div>
                            <div className="mt-1 text-sm font-semibold text-zinc-900">
                              Seçili burger menülerinde %20 indirim
                            </div>
                            <div className="mt-1 text-xs text-zinc-600">
                              Gün boyu geçerli özel kampanya
                            </div>
                          </div>
                        </div>

                        <div className="sticky top-0 z-10 border-b border-zinc-100 bg-white/95 px-4 py-3 backdrop-blur">
                          <div className="flex gap-2 overflow-x-auto pb-1">
                            {previewCategories.map((category, index) => (
                              <button
                                key={category}
                                type="button"
                                className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium ${
                                  index === 0
                                    ? "bg-zinc-900 text-white"
                                    : "bg-zinc-100 text-zinc-700"
                                }`}
                              >
                                {category}
                              </button>
                            ))}
                          </div>
                        </div>

                        <div className="space-y-4 px-4 py-4">
                          {previewItems.map((item) => (
                            <div
                              key={item.name}
                              className="overflow-hidden rounded-3xl border border-zinc-200 bg-white shadow-sm"
                            >
                              <div className="flex gap-3 p-3">
                                <img
                                  src={item.image}
                                  alt={item.name}
                                  className="h-24 w-24 rounded-2xl object-cover"
                                />

                                <div className="min-w-0 flex-1">
                                  <div className="flex items-start justify-between gap-3">
                                    <div>
                                      <h4 className="text-sm font-semibold text-zinc-900">
                                        {item.name}
                                      </h4>
                                      <p className="mt-1 line-clamp-2 text-xs leading-5 text-zinc-500">
                                        {item.desc}
                                      </p>
                                    </div>

                                    <span className="whitespace-nowrap text-sm font-bold text-zinc-900">
                                      {item.price}
                                    </span>
                                  </div>

                                  <div className="mt-3 flex items-center justify-between">
                                    <span className="rounded-full bg-orange-50 px-2.5 py-1 text-[11px] font-medium text-orange-600">
                                      Çok satan
                                    </span>

                                    <button
                                      type="button"
                                      className="inline-flex items-center gap-1 text-xs font-medium text-zinc-700"
                                    >
                                      İncele
                                      <ChevronRight className="h-3.5 w-3.5" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}

                          <div className="rounded-3xl border border-dashed border-zinc-200 bg-zinc-50 px-4 py-5 text-center">
                            <div className="text-sm font-semibold text-zinc-900">
                              Daha fazla ürün ve kategori
                            </div>
                            <div className="mt-1 text-xs text-zinc-500">
                              Tüm içerikler panel üzerinden kolayca yönetilir.
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="border-t border-zinc-100 bg-white px-4 py-3">
                      <div className="flex items-center justify-between text-xs text-zinc-500">
                        <span>QR Menü Önizleme</span>
                        <span>Mobil görünüm</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="how-it-works" className="py-20 scroll-mt-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2
              className="text-3xl font-bold tracking-tight text-zinc-900 md:text-4xl"
              style={{ fontFamily: "Playfair Display, serif" }}
            >
              Nasıl Çalışır?
            </h2>
            <p className="mt-4 text-zinc-600">
              Teknik kurulumla uğraşmadan birkaç adımda dijital menünüzü yayına
              alın.
            </p>
          </div>

          <div className="mt-14 grid gap-6 md:grid-cols-3">
            <div className="rounded-3xl border border-zinc-200 bg-white p-6 text-center shadow-sm">
              <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-orange-100 text-orange-600">
                <Store className="h-6 w-6" />
              </div>
              <div className="mt-4 text-sm font-semibold uppercase tracking-[0.18em] text-orange-500">
                01
              </div>
              <h3
                className="mt-2 text-xl font-semibold text-zinc-900"
                style={{ fontFamily: "Playfair Display, serif" }}
              >
                Kayıt Ol
              </h3>
              <p className="mt-3 text-sm leading-6 text-zinc-600">
                Hesabını oluştur, restoran bilgilerini gir ve 14 günlük ücretsiz
                kullanım süreni başlat.
              </p>
            </div>

            <div className="rounded-3xl border border-zinc-200 bg-white p-6 text-center shadow-sm">
              <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-orange-100 text-orange-600">
                <LayoutDashboard className="h-6 w-6" />
              </div>
              <div className="mt-4 text-sm font-semibold uppercase tracking-[0.18em] text-orange-500">
                02
              </div>
              <h3
                className="mt-2 text-xl font-semibold text-zinc-900"
                style={{ fontFamily: "Playfair Display, serif" }}
              >
                Menünü Yönet
              </h3>
              <p className="mt-3 text-sm leading-6 text-zinc-600">
                Kategorilerini ve ürünlerini ekle. Fiyatlarını, içeriklerini ve
                kampanyalarını panelden kolayca güncelle.
              </p>
            </div>

            <div className="rounded-3xl border border-zinc-200 bg-white p-6 text-center shadow-sm">
              <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-orange-100 text-orange-600">
                <QrCode className="h-6 w-6" />
              </div>
              <div className="mt-4 text-sm font-semibold uppercase tracking-[0.18em] text-orange-500">
                03
              </div>
              <h3
                className="mt-2 text-xl font-semibold text-zinc-900"
                style={{ fontFamily: "Playfair Display, serif" }}
              >
                QR ile Yayınla
              </h3>
              <p className="mt-3 text-sm leading-6 text-zinc-600">
                QR kodunu indir, masalarına yerleştir ve müşterilerinin menüne
                anında ulaşmasını sağla.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section id="features" className="bg-zinc-50 py-20 scroll-mt-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2
              className="text-3xl font-bold tracking-tight text-zinc-900 md:text-4xl"
              style={{ fontFamily: "Playfair Display, serif" }}
            >
              İşletmenize Uygun Güçlü Özellikler
            </h2>
            <p className="mt-4 text-zinc-600">
              Hızlı kurulum, kolay yönetim ve modern müşteri deneyimi için
              ihtiyacınız olan her şey tek panelde.
            </p>
          </div>

          <div className="mt-14 grid gap-8 md:grid-cols-3">
            <div
              className="rounded-3xl border border-zinc-200 bg-white p-8 text-center shadow-sm transition hover:-translate-y-1 hover:shadow-md"
              data-testid="feature-card-qr"
            >
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-orange-100">
                <QrCode className="h-7 w-7 text-orange-600" />
              </div>
              <h3
                className="mt-5 text-xl font-semibold text-zinc-900"
                style={{ fontFamily: "Playfair Display, serif" }}
              >
                QR Kod ile Hızlı Erişim
              </h3>
              <p className="mt-3 leading-relaxed text-zinc-600">
                Restoranınıza özel QR kod oluşturun. Masalara yerleştirin,
                müşterileriniz uygulama indirmeden menünüze ulaşsın.
              </p>
            </div>

            <div
              className="rounded-3xl border border-zinc-200 bg-white p-8 text-center shadow-sm transition hover:-translate-y-1 hover:shadow-md"
              data-testid="feature-card-mobile"
            >
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-orange-100">
                <Smartphone className="h-7 w-7 text-orange-600" />
              </div>
              <h3
                className="mt-5 text-xl font-semibold text-zinc-900"
                style={{ fontFamily: "Playfair Display, serif" }}
              >
                Mobil Uyumlu Deneyim
              </h3>
              <p className="mt-3 leading-relaxed text-zinc-600">
                Hızlı açılan, sade ve modern menüler. iPhone ve Android dahil
                tüm mobil cihazlarda akıcı görünüm sunar.
              </p>
            </div>

            <div
              className="rounded-3xl border border-zinc-200 bg-white p-8 text-center shadow-sm transition hover:-translate-y-1 hover:shadow-md"
              data-testid="feature-card-easy"
            >
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-orange-100">
                <Menu className="h-7 w-7 text-orange-600" />
              </div>
              <h3
                className="mt-5 text-xl font-semibold text-zinc-900"
                style={{ fontFamily: "Playfair Display, serif" }}
              >
                Kolay ve Hızlı Yönetim
              </h3>
              <p className="mt-3 leading-relaxed text-zinc-600">
                Menü öğelerini, kategorileri, fiyatları ve kampanyaları panelden
                dilediğiniz an düzenleyin. Her şey sizin kontrolünüzde.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section id="pricing" className="bg-white py-20 scroll-mt-24">
  <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
    <div className="mx-auto max-w-2xl text-center">
      <h2
        className="text-3xl font-bold tracking-tight text-zinc-900 md:text-4xl"
        style={{ fontFamily: "Playfair Display, serif" }}
      >
        Abonelik Planları
      </h2>
      <p className="mt-4 text-zinc-600">
        İşletmeniz için en uygun paketi seçin. Tüm planlarda QR menü, panel
        yönetimi ve mobil uyumlu menü deneyimi dahildir.
      </p>
    </div>

    <div className="mx-auto mt-12 max-w-6xl rounded-[2.25rem] border border-zinc-200 bg-white p-6 shadow-[0_25px_80px_rgba(15,23,42,0.08)] md:p-10">
      <div className="mb-10 flex items-center gap-4">
        <div className="grid h-16 w-16 shrink-0 place-items-center rounded-3xl bg-zinc-100 text-zinc-900">
          <Wallet className="h-7 w-7" />
        </div>

        <div>
          <h3 className="text-xl font-bold uppercase tracking-wide text-zinc-900">
            Abonelik Planları
          </h3>
          <p className="mt-1 text-zinc-500">
            İşletmen için en uygun paketi seç.
          </p>
        </div>
      </div>

      <div className="grid gap-7 md:grid-cols-3">
        {[
          {
            name: "3 Aylık Plan",
            desc: "3 Aylık Plan, %10 indirim ile",
            price: "949 ₺",
            period: "3 aylık paket",
            monthly: "316 ₺",
          },
          {
            name: "6 Aylık Plan",
            desc: "6 Aylık Plan, %15 indirim ile",
            price: "1.789 ₺",
            period: "6 aylık paket",
            monthly: "298 ₺",
          },
          {
            name: "1 Yıllık Plan",
            desc: "1 Yıllık Plan, %25 indirim ile",
            price: "3.299 ₺",
            period: "12 aylık paket",
            monthly: "275 ₺",
            popular: true,
          },
        ].map((plan) => (
          <div
            key={plan.name}
            className={`relative flex min-h-[420px] flex-col rounded-[1.9rem] border p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_25px_70px_rgba(15,23,42,0.12)] ${
              plan.popular
                ? "border-emerald-400 bg-emerald-50/40 shadow-[0_20px_60px_rgba(16,185,129,0.13)]"
                : "border-zinc-200 bg-white"
            }`}
          >
            {plan.popular && (
              <div className="mb-5 inline-flex w-fit rounded-full bg-emerald-100 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-emerald-700">
                En Popüler
              </div>
            )}

            <div>
              <h4 className="text-2xl font-bold leading-tight text-zinc-900">
                {plan.name}
              </h4>

              <p className="mt-4 min-h-[56px] text-base leading-7 text-zinc-500">
                {plan.desc}
              </p>
            </div>

            <div className="mt-auto pt-10">
              <div className="text-5xl font-black tracking-tight text-zinc-950">
                {plan.price}
              </div>

              <div className="mt-2 text-base text-zinc-500">
                {plan.period}
              </div>

              <div className="mt-8 border-t border-zinc-200 pt-5">
                <div className="text-xs font-semibold uppercase tracking-[0.22em] text-zinc-400">
                  Aylık Karşılık
                </div>

                <div className="mt-2 text-2xl font-black text-zinc-950">
                  {plan.monthly}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 grid gap-3 sm:grid-cols-2">
        <Button
          onClick={() => navigate("/register")}
          className="h-12 rounded-full bg-zinc-900 text-white hover:bg-zinc-800"
        >
          14 Gün Ücretsiz Başla
        </Button>

        <Button
          variant="outline"
          onClick={openWhatsapp}
          className="h-12 rounded-full border-zinc-300 hover:bg-zinc-50"
        >
          Paketler Hakkında Bilgi Al
        </Button>
      </div>
    </div>
  </div>
      </section>

      <section id="trial" className="py-20 scroll-mt-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="overflow-hidden rounded-[2rem] bg-zinc-900 px-6 py-12 text-center text-white shadow-sm md:px-12">
            <div className="mx-auto max-w-3xl">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-medium text-zinc-200">
                <BadgeCheck className="h-4 w-4" />
                Ücretsiz deneme fırsatı
              </div>

              <h2
                className="mt-5 text-3xl font-bold tracking-tight md:text-4xl"
                style={{ fontFamily: "Playfair Display, serif" }}
              >
                14 Gün Boyunca Tüm Özellikleri Deneyin
              </h2>

              <p className="mt-4 text-base leading-7 text-zinc-300 md:text-lg">
                Menü yönetimini, QR kod oluşturmayı ve panel kullanımını
                ücretsiz deneyin. Beğenirseniz size uygun planla devam edin.
              </p>

              <div className="mt-8 flex flex-col justify-center gap-4 sm:flex-row">
                <Button
                  size="lg"
                  onClick={() => navigate("/register")}
                  className="h-12 rounded-full bg-orange-500 px-8 text-white hover:bg-orange-600"
                >
                  Hemen Başla
                </Button>

                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => navigate("/login")}
                  className="h-12 rounded-full border-white/20 bg-transparent px-8 text-white hover:bg-white/10"
                >
                  Hesabıma Giriş Yap
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 text-center">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <h2
            className="text-3xl font-bold tracking-tight text-zinc-900 md:text-4xl"
            style={{ fontFamily: "Playfair Display, serif" }}
            data-testid="cta-heading"
          >
            Menülerinizi Dijitalleştirmeye Hazır mısınız?
          </h2>

          <p className="mt-5 text-lg text-zinc-600">
            QR Menü ile restoranınıza modern bir deneyim kazandırın. Kurulumu
            tamamlayın, menünüzü yayınlayın ve müşterilerinize daha hızlı hizmet
            sunun.
          </p>

          <div className="mt-8 flex flex-col justify-center gap-4 sm:flex-row">
            <Button
              size="lg"
              onClick={() => navigate("/register")}
              className="h-12 rounded-full bg-zinc-900 px-8 text-base text-white transition-all active:scale-95 hover:bg-zinc-800"
              data-testid="cta-create-menu-button"
            >
              14 Gün Ücretsiz Başla
            </Button>

            <Button
              size="lg"
              variant="outline"
              onClick={openWhatsapp}
              className="h-12 rounded-full border-zinc-300 px-8 text-base hover:bg-zinc-50"
            >
              Bizimle İletişime Geç
            </Button>
          </div>
        </div>
      </section>

      <footer className="border-t border-zinc-100 py-8">
        <div className="mx-auto max-w-7xl px-4 text-center text-sm text-zinc-500 sm:px-6 lg:px-8">
          <p>
            © {new Date().getFullYear()} powered by{" "}
            <a
              href="https://megriva.com"
              target="_blank"
              rel="noreferrer"
              className="font-medium text-zinc-700 transition hover:text-zinc-900"
            >
              megriva
            </a>
            . Tüm hakları saklıdır.
          </p>
        </div>
      </footer>
    </div>
  );
}